import type { BoundingBox, FieldPlan, PlanCell, PlotPlan } from '../types/plan';
import type { SaveWorldEntities, WorldBed, WorldCell, WorldCellRef, WorldStructuralObject } from '../types/saves';

const boundingBoxFor = (cells: PlanCell[]) => {
  if (cells.length === 0) return null;
  const rows = cells.map((cell) => cell.row);
  const cols = cells.map((cell) => cell.col);
  const minRow = Math.min(...rows);
  const maxRow = Math.max(...rows);
  const minCol = Math.min(...cols);
  const maxCol = Math.max(...cols);
  return {
    minRow,
    maxRow,
    minCol,
    maxCol,
    size: {
      height: maxRow - minRow + 1,
      width: maxCol - minCol + 1,
    },
  };
};

const buildSingleCellBoundingBox = (row: number, col: number): BoundingBox => ({
  minRow: row,
  maxRow: row,
  minCol: col,
  maxCol: col,
  size: { height: 1, width: 1 },
});

const mapTerrainTypeToPlanKind = (cell: WorldCell): PlanCell['kind'] => {
  switch (String(cell.type || '').toLowerCase()) {
    case 'path':
      return 'path';
    case 'obstacle':
    case 'water':
      return 'obstacle';
    case 'empty':
      return 'empty';
    default:
      return 'empty';
  }
};

const sortCells = (cells: PlanCell[]): PlanCell[] => cells
  .slice()
  .sort((left, right) => (left.row - right.row) || (left.col - right.col));

const cellKey = (row: number, col: number): string => `${row}:${col}`;

const originForPlot = (plot: SaveWorldEntities['plots'][number]): WorldCellRef => ({
  row: plot.plan?.cellOrigin?.row ?? 1,
  col: plot.plan?.cellOrigin?.col ?? 1,
});

const mapStructuralObject = (item: WorldStructuralObject) => ({
  id: item.id,
  name: item.name,
  kind: (item.kind || 'infrastructure') as any,
  subtype: item.subtype || 'generic',
  parameters: item.parameters || {},
  activeFrom: item.activeFrom ?? null,
  activeTo: item.activeTo ?? null,
});

const overlayStructuralObjects = (
  cellsByKey: Map<string, PlanCell>,
  structuralObjects: WorldStructuralObject[],
) => {
  structuralObjects.forEach((structuralObject) => {
    structuralObject.geometry?.cells?.forEach((cell) => {
      const key = cellKey(cell.row, cell.col);
      const existing = cellsByKey.get(key);
      if (existing) {
        existing.structuralObjectId = structuralObject.id;
        return;
      }

      cellsByKey.set(key, {
        row: cell.row,
        col: cell.col,
        kind: 'empty',
        structuralObjectId: structuralObject.id,
      });
    });
  });
};

export const buildPlotPlanFromWorld = (
  structure: SaveWorldEntities,
  plotId: string,
): PlotPlan | null => {
  const plot = structure.plots.find((item) => String(item.id) === String(plotId));
  if (!plot) return null;

  const origin = originForPlot(plot);
  const beds = structure.beds.filter((bed) => String(bed.plotId) === String(plotId));
  const bedByCell = new Map<string, WorldBed>();
  beds.forEach((bed) => {
    bed.cells.forEach((cell) => {
      bedByCell.set(cellKey(origin.row + cell.row, origin.col + cell.col), bed);
    });
  });

  const cellsByKey = new Map<string, PlanCell>();
  (Array.isArray(plot.cells) ? plot.cells : []).forEach((cell) => {
    const row = origin.row + cell.row;
    const col = origin.col + cell.col;
    const key = cellKey(row, col);
    cellsByKey.set(key, {
      row,
      col,
      kind: mapTerrainTypeToPlanKind(cell),
      plotId: plot.id,
    });
  });

  beds.forEach((bed) => {
    bed.cells.forEach((cell) => {
      const row = origin.row + cell.row;
      const col = origin.col + cell.col;
      const key = cellKey(row, col);
      const existing = cellsByKey.get(key);
      if (existing) {
        existing.kind = 'bed';
        existing.bedId = bed.id;
        existing.plotId = plot.id;
        return;
      }
      cellsByKey.set(key, { row, col, kind: 'bed', bedId: bed.id, plotId: plot.id });
    });
  });

  const structuralObjects = structure.structuralObjects.filter((item) => String(item.plotId) === String(plotId));
  overlayStructuralObjects(cellsByKey, structuralObjects);

  const cells = sortCells(Array.from(cellsByKey.values()));
  const boundingBox = plot.plan?.boundingBox ?? boundingBoxFor(cells) ?? buildSingleCellBoundingBox(origin.row, origin.col);
  const allowedBoundingBox = plot.plan?.allowedBoundingBox ?? boundingBox;
  const globalGrid = plot.plan?.globalGrid ?? { size: 3072, maxBoundingBox: 1024 };

  return {
    plotId: plot.id,
    boundingBox,
    allowedBoundingBox,
    globalGrid,
    cells,
    structuralObjects: structuralObjects.map(mapStructuralObject),
    beds: beds.map((bed) => ({
      id: bed.id,
      name: bed.name,
      area: bed.area,
      areaM2: bed.areaM2 ?? bed.area ?? null,
      cellCount: bed.cellCount ?? bed.cells.length,
      areaDisplay: bed.areaDisplay ?? null,
      parameters: bed.parameters || {},
    })),
  };
};

export const buildFieldPlanFromWorld = (
  structure: SaveWorldEntities,
  fieldId: string,
): FieldPlan | null => {
  const field = structure.fields.find((item) => String(item.id) === String(fieldId));
  if (!field) return null;

  const planCells = field.plan?.cells ?? [];
  const cells: PlanCell[] = sortCells(planCells.map((cell) => ({
    row: cell.row,
    col: cell.col,
    kind: 'plot',
    plotId: cell.plotId ?? null,
  })));
  const boundingBox = field.plan?.boundingBox ?? boundingBoxFor(cells);
  const allowedBoundingBox = field.plan?.allowedBoundingBox ?? {
    minRow: 1,
    maxRow: 8,
    minCol: 1,
    maxCol: 8,
  };
  const globalGrid = field.plan?.globalGrid ?? { size: 256, maxBoundingBox: 8 };

  return {
    fieldId,
    boundingBox,
    allowedBoundingBox,
    globalGrid,
    cells,
    structuralObjects: [],
    beds: [],
  };
};
